import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  CloseButton,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowDownToArc,
  IconHandGrab,
  IconPencil,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { useBalanceTypes, useLocationTypes } from '../lookups/useLookups';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listLocations, type LocationDto } from './api';
import { LocationFormModal } from './LocationFormModal';

type Scope = 'active' | 'all';

export function LocationsRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<LocationDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');
  const [search, setSearch] = useState('');
  // The warehouse comes from the header. There was an "All warehouses" option; it cannot mean
  // that any more — a bin list pooled across sites is not something anybody works from, and the
  // API refuses a warehouse other than the one the session is in.
  const warehouseId = useCurrentWarehouseId();

  const locationTypes = useLocationTypes();
  const balanceTypes = useBalanceTypes();

  // A warehouse can hold thousands of bin locations, so the warehouse filter is
  // applied server-side (unlike items/warehouses, which fetch the whole set).
  // Active/All and search stay client-side within the fetched slice.
  const query = useQuery({
    queryKey: ['locations', warehouseId] as const,
    queryFn: ({ signal }) => listLocations({ warehouseId, includeInactive: true }, signal),
  });

  const locationTypeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const lt of locationTypes.data ?? []) map.set(lt.locationTypeId, lt.locationTypeCode);
    return map;
  }, [locationTypes.data]);

  const balanceTypeLabel = useMemo(() => {
    const map = new Map<number, string>();
    for (const bt of balanceTypes.data ?? []) map.set(bt.balanceTypeId, bt.balanceTypeCode);
    return map;
  }, [balanceTypes.data]);

  const pickableTypes = useMemo(() => {
    const map = new Map<string, { isPickable: boolean; isReceivable: boolean }>();
    for (const lt of locationTypes.data ?? []) {
      map.set(lt.locationTypeId, { isPickable: lt.isPickable, isReceivable: lt.isReceivable });
    }
    return map;
  }, [locationTypes.data]);

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((l) => {
      if (scope === 'active' && !l.isActive) return false;
      if (!q) return true;
      return (
        l.locationCode.toLowerCase().includes(q) ||
        (l.displayName ?? '').toLowerCase().includes(q)
      );
    });
  }, [query.data, scope, search]);

  const inactiveCount = useMemo(
    () => (query.data ?? []).filter((l) => !l.isActive).length,
    [query.data],
  );

  function startCreate() {
    setEditing(null);
    open();
  }
  function startEdit(l: LocationDto) {
    setEditing(l);
    open();
  }
  function onSaved() {
    // Any warehouse slice may now be stale (a location could have been created
    // in a warehouse other than the one being viewed).
    void queryClient.invalidateQueries({ queryKey: ['locations'] });
  }

  /**
   * The warehouse column stays conditional — it only earns its place when more than one is in view
   * — which a filtered array expresses as directly as the old && inside the header row did.
   *
   * The capability badges are derived per row from the location TYPE, so that lookup stays inside
   * the renderer rather than being hoisted: it is a Map read, not work.
   */
  const columns: DataColumn<LocationDto>[] = useMemo(() => {
    // Annotated on the ARRAY, not only on the const: an array literal followed by
    // .filter() gets no contextual type from the outer annotation, so every `align` and
    // `card` widens to string and null stays in the element union.
    const all: (DataColumn<LocationDto> | null)[] = [
      {
        key: 'code',
        label: t('locations.code'),
        card: 'title',
        render: (l: LocationDto) => <Code>{l.locationCode}</Code>,
      },
      {
        key: 'name',
        label: t('locations.name'),
        card: 'subtitle',
        render: (l: LocationDto) => l.displayName ?? '—',
      },
      {
        key: 'locationType',
        label: t('locations.locationType'),
        render: (l: LocationDto) => locationTypeLabel.get(l.locationTypeId) ?? '—',
      },
      {
        key: 'balanceType',
        label: t('locations.balanceType'),
        render: (l: LocationDto) => balanceTypeLabel.get(l.balanceTypeId) ?? l.balanceTypeId,
      },
      {
        // Walk order (065). On the list rather than only in the form, because the question it
        // answers is about the aisle as a whole: a route makes sense or it does not, and that is
        // only visible with the locations side by side.
        key: 'sequence',
        label: t('locations.sequence'),
        render: (l: LocationDto) =>
          l.sequence === null ? (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ) : (
            <Text size="sm">{l.sequence}</Text>
          ),
      },
      {
        key: 'flags',
        label: t('locations.flags'),
        render: (l: LocationDto) => {
          const caps = pickableTypes.get(l.locationTypeId);
          return (
            <Group gap={4} wrap="nowrap" justify="flex-end">
              {l.isForwardPick && (
                <Tooltip label={t('locations.isForwardPick')}>
                  <Badge size="sm" variant="light" color="invetrak">
                    {t('locations.forwardPickShort')}
                  </Badge>
                </Tooltip>
              )}
              {caps?.isPickable && (
                <Tooltip label={t('locations.pickable')}>
                  <Badge
                    size="sm"
                    variant="light"
                    color="teal"
                    leftSection={<IconHandGrab size={10} />}
                  >
                    {t('locations.pickableShort')}
                  </Badge>
                </Tooltip>
              )}
              {caps?.isReceivable && (
                <Tooltip label={t('locations.receivable')}>
                  <Badge
                    size="sm"
                    variant="light"
                    color="grape"
                    leftSection={<IconArrowDownToArc size={10} />}
                  >
                    {t('locations.receivableShort')}
                  </Badge>
                </Tooltip>
              )}
              {!l.isForwardPick && !caps?.isPickable && !caps?.isReceivable && (
                <Text size="xs" c="dimmed">
                  —
                </Text>
              )}
            </Group>
          );
        },
      },
      {
        key: 'status',
        label: t('locations.status'),
        card: 'aside',
        render: (l: LocationDto) => (
          <Badge color={l.isActive ? 'green' : 'gray'} variant="light">
            {l.isActive ? t('locations.activeYes') : t('locations.activeNo')}
          </Badge>
        ),
      },
    ];
    return all.filter((c): c is DataColumn<LocationDto> => c !== null);
  },
    [
      t,
      locationTypeLabel,
      balanceTypeLabel,
      pickableTypes,
    ],
  );

  return (
    <>
      <PageHeader
        title={t('nav.locations')}
        actions={
          <Button
            color="invetrak"
            leftSection={<IconPlus size={16} />}
            onClick={startCreate}
          >
            {t('locations.new')}
          </Button>
        }
      />


      {query.isLoading && (
        <Center py="xl">
          <Loader color="invetrak" />
        </Center>
      )}

      {query.isError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title={t('locations.errors.loadFailed')}
        >
          <Stack gap="xs">
            <Text size="sm">{(query.error as Error).message}</Text>
            {query.error instanceof ApiError && query.error.problem.correlationId && (
              <Text size="xs" c="dimmed">
                {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
              </Text>
            )}
            <Group>
              <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                {t('locations.retry')}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {query.isSuccess && (
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Group gap="sm" wrap="wrap">
              <SegmentedControl
                value={scope}
                onChange={(v) => setScope(v as Scope)}
                data={[
                  { label: t('locations.scopeActive'), value: 'active' },
                  {
                    label:
                      inactiveCount > 0
                        ? t('locations.scopeAllCount', { count: inactiveCount })
                        : t('locations.scopeAll'),
                    value: 'all',
                  },
                ]}
              />
              <Text size="sm" c="dimmed">
                {t('locations.count', { count: rows.length })}
              </Text>
            </Group>
            <TextInput
              placeholder={t('locations.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              rightSection={
                search ? (
                  <CloseButton
                    size="sm"
                    onClick={() => setSearch('')}
                    aria-label={t('locations.searchClear')}
                  />
                ) : null
              }
              w={{ base: '100%', sm: 260 }}
            />
          </Group>

          <Card withBorder radius="md" padding={0}>
            {rows.length === 0 ? (
              <Center py="xl">
                <Stack gap="xs" align="center">
                  <Text c="dimmed" size="sm">
                    {search.trim()
                      ? t('locations.noResults')
                      : (query.data ?? []).length === 0
                        ? t('locations.emptyBody')
                        : t('locations.noActive')}
                  </Text>
                  {!search.trim() && scope === 'active' && inactiveCount > 0 && (
                    <Button
                      size="xs"
                      variant="light"
                      color="invetrak"
                      onClick={() => setScope('all')}
                    >
                      {t('locations.showAll')}
                    </Button>
                  )}
                  {!search.trim() && (query.data ?? []).length === 0 && (
                    <Button
                      size="xs"
                      color="invetrak"
                      leftSection={<IconPlus size={14} />}
                      onClick={startCreate}
                    >
                      {t('locations.new')}
                    </Button>
                  )}
                </Stack>
              </Center>
            ) : (
              <ResponsiveTable
                rows={rows}
                columns={columns}
                rowKey={(l) => l.locationId}
                minWidth={720}
                actionsWidth={60}
                highlightOnHover
                verticalSpacing="sm"
                horizontalSpacing="md"
                actions={(l) => (
                  <ActionIcon
                    variant="subtle"
                    color="invetrak"
                    aria-label={t('shell.edit')}
                    onClick={() => startEdit(l)}
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                )}
              />
            )}
          </Card>
        </Stack>
      )}

      <LocationFormModal
        opened={modalOpen}
        onClose={close}
        location={editing}
        onSaved={onSaved}
      />
    </>
  );
}
